/**
 * LifecycleStepper (R.4.1) — the generalized horizontal tracked-state visual from
 * operator-cop-doctrine §3B.5. Renders an ordered lifecycle as connected steps:
 * completed steps solid, the current step emphasized (act register), future steps
 * muted. Each step can carry a timestamp + responsible person surfaced on hover.
 * An optional urgency left-bar (routine/urgent/immediate) mirrors the queue sort
 * key — immediate gets the act-register treatment.
 *
 * Generalized from the Assistance Request lifecycle (§4.20) and reused by the R.3
 * resource-request sub-section (§4.18). The component is presentation-only; the
 * owning panel validates transitions and calls the API.
 */
import type { JSX } from 'react';

export type StepStatus = 'complete' | 'current' | 'future';

export interface LifecycleStep {
  /** Short label, e.g. "Assigned". */
  label: string;
  status: StepStatus;
  /** Optional ISO/display timestamp shown under the label + on hover. */
  timestamp?: string | null;
  /** Optional responsible person/destination, shown on hover. */
  detail?: string | null;
}

export type StepperUrgency = 'routine' | 'urgent' | 'immediate';

const URGENCY_BAR: Record<StepperUrgency, string> = {
  immediate: 'bg-rose-500',
  urgent: 'bg-amber-500',
  routine: 'bg-slate-600',
};

const DOT_CLS: Record<StepStatus, string> = {
  complete: 'bg-emerald-500 border-emerald-400 text-slate-900',
  current: 'bg-sky-500 border-sky-300 text-white ring-2 ring-sky-400/50',
  future: 'bg-slate-800 border-slate-600 text-slate-500',
};

const LABEL_CLS: Record<StepStatus, string> = {
  complete: 'text-emerald-300',
  current: 'text-sky-200 font-semibold',
  future: 'text-slate-500',
};

const CONNECTOR_CLS: Record<StepStatus, string> = {
  complete: 'bg-emerald-500/70',
  current: 'bg-slate-600',
  future: 'bg-slate-700',
};

export function LifecycleStepper({
  steps,
  urgency,
  onTransition,
  'data-testid': testId,
}: {
  steps: LifecycleStep[];
  urgency?: StepperUrgency;
  /** Called with the step index when a clickable forward step is activated. */
  onTransition?: (index: number) => void;
  'data-testid'?: string;
}): JSX.Element {
  const currentIdx = steps.findIndex((s) => s.status === 'current');

  return (
    <div data-testid={testId ?? 'lifecycle-stepper'} className="flex items-stretch gap-2">
      {urgency && (
        <div
          data-testid="lifecycle-stepper-urgency"
          className={`w-1 rounded-full shrink-0 ${URGENCY_BAR[urgency]}`}
          title={`Urgency: ${urgency}`}
        />
      )}
      <ol className="flex flex-1 items-start">
        {steps.map((step, i) => {
          // A step is the actionable "next" step iff it is the first future step
          // immediately after the current one (forward-only).
          const isNextForward =
            step.status === 'future' && i === currentIdx + 1 && Boolean(onTransition);
          const hoverTitle = [step.label, step.timestamp ?? undefined, step.detail ?? undefined]
            .filter(Boolean)
            .join(' · ');
          return (
            <li
              key={step.label}
              data-testid={`lifecycle-step-${step.label.toLowerCase().replace(/\s+/g, '-')}`}
              data-status={step.status}
              className="flex flex-1 flex-col items-center min-w-0"
            >
              <div className="flex items-center w-full">
                {/* left connector (hidden on first) */}
                <div
                  className={`h-0.5 flex-1 ${i === 0 ? 'opacity-0' : CONNECTOR_CLS[step.status]}`}
                />
                {isNextForward ? (
                  <button
                    type="button"
                    data-testid={`lifecycle-advance-${step.label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => onTransition?.(i)}
                    title={`Advance to ${step.label}`}
                    className={`flex h-6 w-6 items-center justify-center rounded-full border text-[10px] font-bold ${DOT_CLS[step.status]} hover:bg-sky-600 hover:text-white hover:border-sky-400 cursor-pointer transition-colors`}
                  >
                    {i + 1}
                  </button>
                ) : (
                  <div
                    title={hoverTitle}
                    className={`flex h-6 w-6 items-center justify-center rounded-full border text-[10px] font-bold ${DOT_CLS[step.status]}`}
                  >
                    {step.status === 'complete' ? '✓' : i + 1}
                  </div>
                )}
                {/* right connector (hidden on last) */}
                <div
                  className={`h-0.5 flex-1 ${
                    i === steps.length - 1
                      ? 'opacity-0'
                      : CONNECTOR_CLS[steps[i + 1]?.status ?? 'future']
                  }`}
                />
              </div>
              <span
                title={hoverTitle}
                className={`mt-1 text-[10px] text-center leading-tight truncate w-full px-0.5 ${LABEL_CLS[step.status]}`}
              >
                {step.label}
              </span>
              {step.timestamp && (
                <span className="text-[9px] text-slate-500 text-center leading-tight">
                  {step.timestamp}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
